import { computed, ref, watch, type Ref } from 'vue'
import { normalizeCardJsonV1 } from '../../data/cardsApi'
import { unwrapResponseJson, wpFetch } from '../../data/wpApi'
import type { FlashcardElementInput } from '../../flashcard/elements/registry'
import { joinPerlingoApi, resolvePerlingoRestContext } from '../api/deckPlayerApi'

type Props = {
  deckId: string
  model: string
  restRoot?: string
  nonce?: string
  passthroughProps?: Record<string, unknown>
}

export type ApiError = { code?: string; message?: string; missingCardIds?: string[] }

type ProbeResult = {
  cardId: string
  postId: number | null
  url?: string
  ok: boolean
  status?: number
  hasCheckboxGroup?: boolean
  elementTypesFront?: { type: string; count: number }[]
  elementTypesBack?: { type: string; count: number }[]
  error?: string
}

export const toErrorString = (err: unknown) => {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  try {
    return JSON.stringify(err)
  } catch {
    return String(err)
  }
}

const asRecord = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null

/**
 * Error details from a failed bootstrap: WordPress REST errors ({ code, message }) or
 * Perlingo's own format ({ error: { code, message, missingCardIds } }).
 */
export function extractApiError(err: unknown): ApiError | null {
  // Check the raw value first: unwrapResponseJson() strips code/message next to "data".
  const raw = asRecord(err)
  if (raw && (typeof raw.code === 'string' || typeof raw.message === 'string')) {
    return {
      code: typeof raw.code === 'string' ? raw.code : undefined,
      message: typeof raw.message === 'string' ? raw.message : undefined,
    }
  }
  const errorObject = asRecord(asRecord(unwrapResponseJson(err))?.error)
  if (!errorObject) return null
  return {
    code: typeof errorObject.code === 'string' ? errorObject.code : undefined,
    message: typeof errorObject.message === 'string' ? errorObject.message : undefined,
    missingCardIds: Array.isArray(errorObject.missingCardIds)
      ? errorObject.missingCardIds.map(String)
      : undefined,
  }
}

/** Last number in an id like "card-123". */
const parseNumericId = (id: string): number | null => {
  const m = String(id).match(/(\d+)/g)
  if (!m || m.length === 0) return null
  const n = Number(m[m.length - 1])
  return Number.isFinite(n) ? n : null
}

const summarizeElementTypes = (elements: FlashcardElementInput[]) => {
  const types = new Map<string, number>()
  for (const el of elements) {
    const type = (el as { type?: unknown })?.type
    if (typeof type === 'string') types.set(type, (types.get(type) ?? 0) + 1)
  }
  return Array.from(types.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([type, count]) => ({ type, count }))
}

const hasCheckboxGroup = (front: FlashcardElementInput[], back: FlashcardElementInput[]) => {
  const isGroup = (e: FlashcardElementInput) => (e as { type?: unknown })?.type === 'checkbox-group'
  return front.some(isGroup) || back.some(isGroup)
}

/** Runs async tasks with at most `concurrency` in flight. */
async function runWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  concurrency: number,
): Promise<T[]> {
  const results: T[] = []
  const pending = [...tasks]
  const workers = Array.from({ length: Math.max(1, concurrency) }, async () => {
    while (pending.length) {
      const task = pending.shift()
      if (!task) break
      results.push(await task())
    }
  })
  await Promise.all(workers)
  return results
}

const copyText = async (text: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  if (typeof document === 'undefined') throw new Error('Clipboard API not available')
  const area = document.createElement('textarea')
  area.value = text
  area.style.position = 'fixed'
  area.style.left = '-9999px'
  area.style.top = '0'
  document.body.appendChild(area)
  area.focus()
  area.select()
  document.execCommand('copy')
  document.body.removeChild(area)
}

/**
 * Helps authors understand a failed deck load: lists cards that make a deck
 * incompatible (with titles) and copies a debug report for support.
 */
export function useDeckErrorDiagnostics(props: Props, shownError: Ref<string | null>) {
  const lastErrorRaw = ref<unknown>(null)
  const lastApiError = ref<ApiError | null>(null)
  const copyState = ref<'idle' | 'copying' | 'copied' | 'failed'>('idle')
  const copyFailureMessage = ref<string | null>(null)
  const detailsOpen = ref(false)
  const missingCardsLoading = ref(false)
  const missingCards = ref<
    Array<{ cardId: string; postId: number | null; title?: string; error?: string }>
  >([])
  const probeResults = ref<ProbeResult[] | null>(null)

  const missingCardIds = computed(() => lastApiError.value?.missingCardIds ?? [])
  const canShowMissingCards = computed(
    () => lastApiError.value?.code === 'deck_not_compatible' && missingCardIds.value.length > 0,
  )

  const restContext = () => {
    try {
      return resolvePerlingoRestContext({ restRoot: props.restRoot, nonce: props.nonce })
    } catch {
      return null
    }
  }

  const fetchCardDocument = async (postId: number) => {
    const ctx = restContext()
    if (!ctx) return null
    const url = joinPerlingoApi(ctx.restRoot, `/cards/${encodeURIComponent(String(postId))}/json`)
    const response = await wpFetch(url, { method: 'GET' }, ctx.nonce)
    return { url, response }
  }

  const fetchCardTitle = async (postId: number): Promise<string | null> => {
    const fetched = await fetchCardDocument(postId)
    if (!fetched?.response.ok) return null
    const normalized = normalizeCardJsonV1<FlashcardElementInput>(
      (await fetched.response.json()) as unknown,
    )
    return normalized?.post?.title ? normalized.post.title : null
  }

  const loadMissingCards = async () => {
    if (!canShowMissingCards.value || missingCardsLoading.value || missingCards.value.length) return
    missingCardsLoading.value = true
    try {
      const ids = missingCardIds.value
      // Show every id, but fetch titles only for a subset of huge decks.
      const maxTitleFetch = ids.length <= 15 ? ids.length : 30
      const base = ids.map((cardId) => ({ cardId, postId: parseNumericId(cardId) }))
      const tasks = base.slice(0, maxTitleFetch).map((item) => async () => {
        if (!item.postId) return { ...item, error: 'Could not parse post id' }
        try {
          const title = await fetchCardTitle(item.postId)
          return { ...item, title: title ?? undefined }
        } catch (e) {
          return { ...item, error: toErrorString(e) }
        }
      })
      const withTitles = await runWithConcurrency(tasks, 6)
      const byCardId = new Map(withTitles.map((x) => [x.cardId, x] as const))
      missingCards.value = base.map((x) => byCardId.get(x.cardId) ?? x)
    } finally {
      missingCardsLoading.value = false
    }
  }

  watch(
    () => detailsOpen.value,
    (open) => {
      if (open) void loadMissingCards()
    },
  )

  /** Fetches up to five missing cards to show what they contain. */
  const probeMissingCards = async (ids: string[]): Promise<ProbeResult[] | null> => {
    if (!ids.length) return null
    const ctx = restContext()
    if (!ctx) return null
    const results: ProbeResult[] = []
    for (const cardId of ids.slice(0, 5)) {
      const postId = parseNumericId(cardId)
      if (!postId) {
        results.push({
          cardId,
          postId: null,
          ok: false,
          error: 'Could not parse numeric postId from cardId',
        })
        continue
      }
      const url = joinPerlingoApi(ctx.restRoot, `/cards/${encodeURIComponent(String(postId))}/json`)
      try {
        const response = await wpFetch(url, { method: 'GET' }, ctx.nonce)
        const normalized = normalizeCardJsonV1<FlashcardElementInput>(
          (await response.json()) as unknown,
        )
        const front = normalized?.frontElements ?? []
        const back = normalized?.backElements ?? []
        results.push({
          cardId,
          postId,
          url,
          ok: response.ok,
          status: response.status,
          hasCheckboxGroup: hasCheckboxGroup(front, back),
          elementTypesFront: summarizeElementTypes(front),
          elementTypesBack: summarizeElementTypes(back),
        })
      } catch (e) {
        results.push({ cardId, postId, url, ok: false, error: toErrorString(e) })
      }
    }
    return results
  }

  const bootstrapUrl = (restRoot: string) => {
    const url = new URL(
      joinPerlingoApi(restRoot, `/decks/${encodeURIComponent(props.deckId)}/player`),
    )
    url.searchParams.set('model', props.model)
    url.searchParams.set('includeCards', 'true')
    return url.toString()
  }

  const copyDebugReport = async () => {
    copyState.value = 'copying'
    copyFailureMessage.value = null
    const resolved = restContext()
    const apiError = lastApiError.value
    const missing = apiError?.missingCardIds ?? []

    // Probe only when it helps.
    let probes = probeResults.value
    if (!probes && apiError?.code === 'deck_not_compatible' && missing.length > 0) {
      probes = await probeMissingCards(missing)
      probeResults.value = probes
    }

    const report = {
      kind: 'PerlingoFlashcards.DeckPlayer.DebugReport',
      timestamp: new Date().toISOString(),
      pageUrl: typeof window !== 'undefined' ? window.location.href : null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      deckId: props.deckId,
      model: props.model,
      restRoot: { prop: props.restRoot ?? null, resolved: resolved?.restRoot ?? null },
      nonceProvided: Boolean(props.nonce || resolved?.nonce),
      passthroughProps: props.passthroughProps ?? {},
      bootstrapRequestUrl: resolved?.restRoot ? bootstrapUrl(resolved.restRoot) : null,
      error: {
        messageShown: shownError.value,
        parsed: apiError,
        raw: unwrapResponseJson(lastErrorRaw.value),
      },
      probes,
    }

    let text: string
    try {
      text = JSON.stringify(report, null, 2)
    } catch {
      text = String(report)
    }
    try {
      await copyText(text)
      copyState.value = 'copied'
      window.setTimeout(() => {
        copyState.value = 'idle'
      }, 1200)
    } catch (e) {
      copyState.value = 'failed'
      copyFailureMessage.value = toErrorString(e)
    }
  }

  /** Clears diagnostics before a new load. */
  const reset = () => {
    lastErrorRaw.value = null
    lastApiError.value = null
    probeResults.value = null
  }

  /** Records a load failure; returns its parsed API error. */
  const record = (err: unknown) => {
    const apiError = extractApiError(err)
    lastErrorRaw.value = err
    lastApiError.value = apiError
    detailsOpen.value = false
    missingCards.value = []
    return apiError
  }

  return {
    copyState,
    copyFailureMessage,
    detailsOpen,
    missingCardsLoading,
    missingCards,
    missingCardIds,
    canShowMissingCards,
    copyDebugReport,
    reset,
    record,
  }
}

export type DeckErrorDiagnostics = ReturnType<typeof useDeckErrorDiagnostics>
